import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, computed, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { remapEchartsColors, watchBodyDarkMode$ } from '@examdojo/core/widget';
import { EChartsOption } from 'echarts';
import { LineChart, ScatterChart } from 'echarts/charts';
import {
  DataZoomInsideComponent,
  DataZoomSliderComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  TooltipComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { SVGRenderer } from 'echarts/renderers';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';

import type { CorrelationHoverSection } from '../../dashboards/correlation.store';
import { fmt, fmtAxisLabel } from '../chart-shared';
import {
  DEFAULT_SCATTER_PLOT_DISPLAY,
  type ScatterPlotDisplayConfig,
  type ScatterPlotParams,
  type ScatterPlotPrediction,
} from './scatter-plot-chart.model';
import { computeRegression, computeResiduals, predictY, type RegressionResult } from './scatter-plot-chart.util';

echarts.use([
  ScatterChart,
  LineChart,
  SVGRenderer,
  GridComponent,
  TooltipComponent,
  MarkLineComponent,
  LegendComponent,
  DataZoomSliderComponent,
  DataZoomInsideComponent,
]);

@Component({
  selector: 'dojo-scatter-plot-chart',
  standalone: true,
  imports: [NgxEchartsDirective],
  providers: [provideEchartsCore({ echarts })],
  templateUrl: './scatter-plot-chart.component.html',
  styleUrl: './scatter-plot-chart.component.scss',
  host: { class: 'block w-full h-full' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScatterPlotChartComponent {
  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    this.isDark = toSignal(watchBodyDarkMode$(this.document), { initialValue: false });
  }

  private readonly isDark;

  readonly params = input.required<ScatterPlotParams>();
  readonly display = input<ScatterPlotDisplayConfig>({});
  readonly prediction = input<ScatterPlotPrediction | null>(null);
  readonly hoveredSection = input<CorrelationHoverSection | null>(null);
  readonly hoveredPointIndex = input<number | null>(null);

  readonly pointHover = output<{ index: number | null; source: 'chart' }>();
  readonly initOpts = { renderer: 'svg' } as const;

  readonly regression = computed<RegressionResult | null>(() => {
    const points = this.params().points;
    return computeRegression(points);
  });

  readonly chartOptions = computed<EChartsOption>(() => {
    const { points, xLabel, yLabel } = this.params();
    const display = { ...DEFAULT_SCATTER_PLOT_DISPLAY, ...this.display() };
    const regression = this.regression();
    const pred = this.prediction();
    const hoveredSection = this.hoveredSection();
    const hoveredPointIndex = this.hoveredPointIndex();

    if (points.length === 0) {
      return buildEmptyChart(xLabel, yLabel);
    }

    // Resolve colors
    const dotColor = display.dotColor ?? '#0ea5e9';
    const regressionColor = display.regressionLineColor ?? '#8b5cf6';
    const residualColor = display.residualColor ?? '#ef4444';

    // Only highlight regression line when hovering the equation itself
    const isRegressionHovered = hoveredSection === 'equation';

    // Compute axis ranges
    const xValues = points.map((p) => p.x);
    const yValues = points.map((p) => p.y);
    const xMin = Math.min(...xValues);
    const xMax = Math.max(...xValues);
    const yMin = Math.min(...yValues);
    const yMax = Math.max(...yValues);
    const xRange = xMax - xMin || 1;
    const yRange = yMax - yMin || 1;
    const xPadding = xRange * 0.08;
    const yPadding = yRange * 0.08;

    let axisXMin = xMin - xPadding;
    let axisXMax = xMax + xPadding;
    let axisYMin = yMin - yPadding;
    let axisYMax = yMax + yPadding;

    // Extend axis to include prediction point (especially for extrapolation)
    if (pred && regression) {
      axisXMin = Math.min(axisXMin, pred.x - xPadding);
      axisXMax = Math.max(axisXMax, pred.x + xPadding);
      axisYMin = Math.min(axisYMin, pred.y - yPadding);
      axisYMax = Math.max(axisYMax, pred.y + yPadding);
    }

    // Extend axis to include y-intercept when intercept is hovered
    if (hoveredSection === 'intercept' && regression) {
      axisXMin = Math.min(axisXMin, -xPadding);
      axisYMin = Math.min(axisYMin, regression.b - yPadding);
      axisYMax = Math.max(axisYMax, regression.b + yPadding);
    }

    const showLegend = !!(display.showRegressionLine || display.showResiduals);
    const zoomX = !!display.dataZoomX;
    const zoomY = !!display.dataZoomY;

    const series: NonNullable<EChartsOption['series']> = [];

    // Scatter points
    const defaultDotSize = display.dotSize ?? 10;
    series.push({
      type: 'scatter',
      name: 'Observation',
      data: points.map((p, i) => ({
        value: [p.x, p.y],
        symbolSize: hoveredPointIndex === i ? defaultDotSize + 6 : defaultDotSize,
        itemStyle:
          hoveredPointIndex === i
            ? { color: '#f59e0b', borderColor: '#fff', borderWidth: 2 }
            : hoveredPointIndex !== null
              ? { color: dotColor, opacity: 0.3 }
              : { color: dotColor },
      })),
      symbolSize: defaultDotSize,
      itemStyle: { color: dotColor },
      z: 3,
    });

    // Regression line
    if (display.showRegressionLine && regression) {
      const lineStart = predictY(regression, axisXMin);
      const lineEnd = predictY(regression, axisXMax);

      series.push({
        type: 'line',
        name: 'Regression Line',
        data: [
          [axisXMin, lineStart],
          [axisXMax, lineEnd],
        ],
        symbol: 'none',
        lineStyle: {
          color: regressionColor,
          width: isRegressionHovered ? 4 : 2,
          shadowBlur: isRegressionHovered ? 8 : 0,
          shadowColor: isRegressionHovered ? regressionColor : 'transparent',
        },
        z: 2,
        silent: true,
      });
    }

    // Residual lines
    if (display.showResiduals && regression) {
      const residuals = computeResiduals(points, regression);

      for (const r of residuals) {
        series.push({
          type: 'line',
          name: 'Residuals',
          data: [
            [r.x, r.actual],
            [r.x, r.predicted],
          ],
          symbol: 'none',
          lineStyle: {
            color: residualColor,
            width: 1.5,
            type: 'dashed',
          },
          z: 1,
          silent: true,
        } as Record<string, unknown>);
      }
    }

    // Prediction point
    if (pred && regression) {
      series.push({
        type: 'scatter',
        name: 'Prediction',
        data: [[pred.x, pred.y]],
        symbolSize: 14,
        symbol: 'diamond',
        itemStyle: {
          color: '#f59e0b',
          borderColor: '#fff',
          borderWidth: 2,
        },
        z: 4,
      });

      series.push({
        type: 'line',
        data: [
          [pred.x, axisYMin],
          [pred.x, pred.y],
        ],
        symbol: 'none',
        lineStyle: { color: '#f59e0b', width: 1, type: 'dashed' },
        z: 1,
        silent: true,
      } as Record<string, unknown>);
      series.push({
        type: 'line',
        data: [
          [axisXMin, pred.y],
          [pred.x, pred.y],
        ],
        symbol: 'none',
        lineStyle: { color: '#f59e0b', width: 1, type: 'dashed' },
        z: 1,
        silent: true,
      } as Record<string, unknown>);
    }

    // ── Slope triangle annotation ──
    if (hoveredSection === 'slope' && regression) {
      const xMid = (xMin + xMax) / 2;
      const deltaX = xRange * 0.35;
      const xRef = xMid - deltaX / 2;
      const yRef = predictY(regression, xRef);
      const xEnd = xRef + deltaX;
      const yEnd = predictY(regression, xEnd);
      const rise = regression.a * deltaX;

      // Horizontal leg (run)
      series.push({
        type: 'line',
        data: [
          [xRef, yRef],
          [xEnd, yRef],
        ],
        symbol: 'none',
        lineStyle: { color: regressionColor, width: 2.5, type: 'dashed' },
        z: 5,
        silent: true,
      } as Record<string, unknown>);

      // Vertical leg (rise)
      series.push({
        type: 'line',
        data: [
          [xEnd, yRef],
          [xEnd, yEnd],
        ],
        symbol: 'none',
        lineStyle: { color: regressionColor, width: 2.5, type: 'dashed' },
        z: 5,
        silent: true,
      } as Record<string, unknown>);

      // Δx label
      series.push({
        type: 'scatter',
        data: [[(xRef + xEnd) / 2, yRef]],
        symbolSize: 0,
        label: {
          show: true,
          formatter: `Δx = ${fmt(deltaX)}`,
          position: regression.a >= 0 ? 'bottom' : 'top',
          fontSize: 12,
          fontWeight: 600,
          color: regressionColor,
          backgroundColor: 'rgba(255,255,255,0.92)',
          padding: [3, 8],
          borderRadius: 4,
        },
        z: 6,
        silent: true,
      } as Record<string, unknown>);

      // Δy label
      series.push({
        type: 'scatter',
        data: [[xEnd, (yRef + yEnd) / 2]],
        symbolSize: 0,
        label: {
          show: true,
          formatter: `Δy = ${fmt(rise)}`,
          position: 'right',
          fontSize: 12,
          fontWeight: 600,
          color: regressionColor,
          backgroundColor: 'rgba(255,255,255,0.92)',
          padding: [3, 8],
          borderRadius: 4,
        },
        z: 6,
        silent: true,
      } as Record<string, unknown>);

      // Slope = Δy / Δx label at corner
      series.push({
        type: 'scatter',
        data: [[xEnd, yRef]],
        symbolSize: 8,
        symbol: 'rect',
        itemStyle: { color: regressionColor },
        label: {
          show: true,
          formatter: `a = Δy/Δx = ${fmt(regression.a)}`,
          position: regression.a >= 0 ? 'bottomRight' : 'topRight',
          fontSize: 12,
          fontWeight: 700,
          color: regressionColor,
          backgroundColor: 'rgba(255,255,255,0.92)',
          padding: [4, 10],
          borderRadius: 4,
        },
        z: 6,
        silent: true,
      } as Record<string, unknown>);
    }

    // ── Intercept dot annotation ──
    if (hoveredSection === 'intercept' && regression) {
      // Y-intercept dot at (0, b)
      series.push({
        type: 'scatter',
        data: [[0, regression.b]],
        symbolSize: 14,
        symbol: 'circle',
        itemStyle: {
          color: regressionColor,
          borderColor: '#fff',
          borderWidth: 2,
        },
        label: {
          show: true,
          formatter: `b = ${fmt(regression.b)}`,
          position: 'right',
          fontSize: 12,
          fontWeight: 700,
          color: regressionColor,
          backgroundColor: 'rgba(255,255,255,0.92)',
          padding: [3, 8],
          borderRadius: 4,
          borderColor: regressionColor,
          borderWidth: 1,
        },
        z: 7,
        silent: true,
      } as Record<string, unknown>);

      // Dotted vertical line at x=0
      series.push({
        type: 'line',
        data: [
          [0, axisYMin],
          [0, regression.b],
        ],
        symbol: 'none',
        lineStyle: { color: regressionColor, width: 1.5, type: 'dotted' },
        z: 5,
        silent: true,
      } as Record<string, unknown>);
    }

    // Data zoom — filterMode 'none' keeps all series visible (regression line spans full range)
    const dataZoomStyle = {
      borderColor: '#e5e7eb',
      fillerColor: 'rgba(14,165,233,0.08)',
      handleStyle: { color: '#0ea5e9', borderColor: '#0ea5e9' },
      dataBackground: { lineStyle: { color: '#d1d5db' }, areaStyle: { color: '#f3f4f6' } },
      selectedDataBackground: { lineStyle: { color: '#0ea5e9' }, areaStyle: { color: 'rgba(14,165,233,0.12)' } },
      textStyle: { fontSize: 10, color: '#9ca3af' },
    };
    const dataZoom: Array<Record<string, unknown>> = [];
    if (zoomX) {
      dataZoom.push({
        type: 'slider',
        xAxisIndex: 0,
        filterMode: 'none',
        bottom: 28,
        height: 20,
        ...dataZoomStyle,
      });
    }
    if (zoomY) {
      dataZoom.push({
        type: 'slider',
        yAxisIndex: 0,
        filterMode: 'none',
        left: 16,
        width: 20,
        ...dataZoomStyle,
      });
    }

    // Tooltip helpers
    const prefixStyle = (color: string) =>
      `font-size:10px;font-style:italic;font-weight:600;color:${color};min-width:12px;text-align:center`;
    const tooltipRow = (
      prefix: string,
      label: string,
      value: string,
      opts?: { prefixColor?: string; valueColor?: string },
    ) => {
      const pc = opts?.prefixColor ?? '#d1d5db';
      const vc = opts?.valueColor ?? '#374151';
      return (
        `<div style="display:flex;align-items:baseline;gap:6px;padding:1px 0">` +
        `<span style="${prefixStyle(pc)}">${prefix}</span>` +
        `<span style="color:#9ca3af;flex:1">${label}</span>` +
        `<span style="font-weight:600;color:${vc};margin-left:12px">${value}</span>` +
        `</div>`
      );
    };
    const tooltipDivider = () => `<div style="border-top:1px solid #f3f4f6;margin:4px 0 3px 18px"></div>`;

    return {
      animationDuration: isRegressionHovered ? 300 : 500,
      tooltip: {
        show: true,
        trigger: 'item',
        confine: true,
        backgroundColor: '#fff',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        extraCssText: 'box-shadow: 0 2px 8px rgba(0,0,0,0.08);',
        padding: [10, 14],
        textStyle: { fontSize: 12, color: '#374151' },
        formatter: (params: unknown) => {
          const p = params as { seriesName?: string; value?: number[] };
          if (!p.value) {
            return '';
          }

          const xVal = p.value[0];
          const yVal = p.value[1];

          if (p.seriesName === 'Prediction') {
            return [tooltipRow('x', xLabel, fmt(xVal)), tooltipRow('y', yLabel, fmt(yVal))].join('');
          }

          const lines: string[] = [];
          lines.push(tooltipRow('x', xLabel, fmt(xVal)));
          lines.push(tooltipRow('y', yLabel, fmt(yVal)));

          // Show predicted value and residual when regression line is visible
          if (display.showRegressionLine && regression) {
            const predicted = predictY(regression, xVal);
            const residual = yVal - predicted;
            const sign = residual >= 0 ? '+' : '';

            lines.push(tooltipDivider());
            lines.push(
              tooltipRow('ŷ', 'Predicted', fmt(predicted), {
                prefixColor: regressionColor,
                valueColor: regressionColor,
              }),
            );
            lines.push(tooltipRow('', 'Residual', `${sign}${fmt(residual)}`, { valueColor: residualColor }));
          }

          return lines.join('');
        },
      },
      legend: {
        show: showLegend,
        top: 0,
        right: 0,
        itemWidth: 16,
        itemHeight: 10,
        itemGap: 16,
        textStyle: { fontSize: 11, color: '#6b7280' },
        data: [
          { name: 'Observation', icon: 'circle' },
          ...(display.showRegressionLine && regression
            ? [{ name: 'Regression Line', icon: 'path://M0,4L16,4L16,6L0,6Z', itemStyle: { color: regressionColor } }]
            : []),
          ...(display.showResiduals && regression
            ? [
                {
                  name: 'Residuals',
                  icon: 'path://M0,4L4,4L4,6L0,6Z M6,4L10,4L10,6L6,6Z M12,4L16,4L16,6L12,6Z',
                  itemStyle: { color: residualColor },
                },
              ]
            : []),
        ],
      },
      dataZoom: dataZoom.length > 0 ? dataZoom : undefined,
      grid: {
        top: showLegend ? 28 : 20,
        bottom: zoomX ? 80 : 45,
        left: zoomY ? 92 : 55,
        right: 25,
      },
      xAxis: {
        type: 'value',
        name: xLabel,
        nameLocation: 'middle',
        nameGap: zoomX ? 64 : 28,
        nameTextStyle: { fontSize: 12, color: '#6b7280' },
        min: zoomX ? undefined : axisXMin,
        max: zoomX ? undefined : axisXMax,
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#d1d5db' } },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
        axisLabel: { fontSize: 12, color: '#6b7280', formatter: fmtAxisLabel },
      },
      yAxis: {
        type: 'value',
        name: yLabel,
        nameLocation: 'middle',
        nameGap: zoomY ? 80 : 42,
        nameTextStyle: { fontSize: 12, color: '#6b7280' },
        min: zoomY ? undefined : axisYMin,
        max: zoomY ? undefined : axisYMax,
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#d1d5db' } },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
        axisLabel: { fontSize: 12, color: '#6b7280', formatter: fmtAxisLabel },
      },
      series,
    };
  });

  readonly adaptedOptions = computed(() => remapEchartsColors(this.chartOptions(), this.document, this.isDark()));

  onChartMouseOver(event: { seriesName?: string; dataIndex?: number }): void {
    // eslint-disable-next-line eqeqeq -- intentional nullish check (null | undefined)
    if (event.seriesName === 'Observation' && event.dataIndex != null) {
      this.pointHover.emit({ index: event.dataIndex, source: 'chart' });
    }
  }

  onChartMouseOut(event: { seriesName?: string }): void {
    if (event.seriesName === 'Observation') {
      this.pointHover.emit({ index: null, source: 'chart' });
    }
  }
}

function buildEmptyChart(xLabel: string, yLabel: string): EChartsOption {
  return {
    grid: { top: 20, bottom: 45, left: 55, right: 25 },
    xAxis: {
      type: 'value',
      name: xLabel,
      nameLocation: 'middle',
      nameGap: 28,
      nameTextStyle: { fontSize: 12, color: '#6b7280' },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#d1d5db' } },
      splitLine: { lineStyle: { color: '#f3f4f6' } },
      axisLabel: { fontSize: 12, color: '#6b7280' },
    },
    yAxis: {
      type: 'value',
      name: yLabel,
      nameLocation: 'middle',
      nameGap: 42,
      nameTextStyle: { fontSize: 12, color: '#6b7280' },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#d1d5db' } },
      splitLine: { lineStyle: { color: '#f3f4f6' } },
      axisLabel: { fontSize: 12, color: '#6b7280' },
    },
    series: [],
  };
}
